/**
 * The browser only talks to the remote-control application contract.  It never
 * receives Codex credentials or forwards arbitrary JSON-RPC calls to a host.
 *
 * This client deliberately keeps the transport small: HTTP performs commands
 * and reads; the socket is an ordered event subscription.  The mock store is
 * useful for a Pages preview before a bridge has been paired, but all callers
 * use the same shapes for mock and live data.
 */

export type Theme = "system" | "light" | "dark";
type Maybe<T> = T | undefined;
export type HostPresence = "online" | "stale" | "offline";
export type TaskRuntimeState =
  | "not_loaded"
  | "idle"
  | "running"
  | "waiting_approval"
  | "waiting_input"
  | "error";

export type Host = {
  hostId: string;
  name: string;
  platform: string;
  adapterMode?: Maybe<"desktop_attached" | "standalone" | "history_readonly">;
  presence: HostPresence;
  lastSeenAt?: Maybe<string>;
  bridgeVersion?: Maybe<string>;
  serverVersion?: Maybe<string>;
  capabilityRevision?: Maybe<string>;
  capabilities?: Maybe<Record<string, Capability>>;
};

export type Capability = {
  state: "available" | "unavailable" | "unknown" | "restricted" | "degraded";
  source?: Maybe<"native" | "bridge" | "composed">;
  reason?: Maybe<string>;
  reasonCode?: Maybe<string>;
  allowedParameters?: Maybe<Record<string, unknown>>;
};

export type Project = {
  projectId: string;
  hostId: string;
  name: string;
  displayPath?: Maybe<string>;
  source?: Maybe<"codex" | "bridge">;
  isGit?: Maybe<boolean>;
  branch?: Maybe<string>;
  taskCount?: Maybe<number>;
};

export type Task = {
  taskId: string;
  hostId: string;
  projectId: string;
  title: string;
  nativeThreadId?: Maybe<string>;
  activeTurnId?: Maybe<string>;
  runtimeState: TaskRuntimeState;
  archived?: Maybe<boolean>;
  pinned?: Maybe<boolean>;
  pinScope?: Maybe<"native" | "remote_only">;
  canAcceptDirectInput?: Maybe<boolean | null>;
  revision?: Maybe<string>;
  updatedAt?: Maybe<string>;
  lastOutcome?: Maybe<"completed" | "failed" | "interrupted">;
  preview?: Maybe<string>;
  unread?: Maybe<boolean>;
  pendingApprovalCount?: Maybe<number>;
};

export type TimelineKind =
  | "user_message"
  | "assistant_message"
  | "reasoning_summary"
  | "plan"
  | "command"
  | "file_change"
  | "tool_call"
  | "tool_result"
  | "approval"
  | "user_input"
  | "image"
  | "artifact"
  | "context_compaction"
  | "unknown";

export type TimelineItem = {
  itemId: string;
  taskId: string;
  turnId?: Maybe<string>;
  sequence?: Maybe<number>;
  kind: TimelineKind;
  state?: Maybe<"streaming" | "complete" | "failed" | "pending">;
  createdAt?: Maybe<string>;
  text?: Maybe<string>;
  title?: Maybe<string>;
  payload?: Maybe<Record<string, unknown>>;
};

export type Approval = {
  approvalId: string;
  taskId: string;
  turnId?: Maybe<string>;
  kind: "command" | "file_change" | "permissions" | "user_input" | "mcp_elicitation";
  state: "pending" | "responding" | "resolved" | "expired" | "unavailable";
  revision?: Maybe<string>;
  payload?: Maybe<Record<string, unknown>>;
  availableDecisions: string[];
};

export type ModelOption = {
  modelId: string;
  label: string;
  description?: Maybe<string>;
  reasoningEfforts: string[];
  defaultEffort?: Maybe<string>;
  serviceTiers: Array<{ id: string; name: string; description?: Maybe<string> }>;
  defaultServiceTier?: Maybe<string>;
  modalities?: Maybe<string[]>;
  available: boolean;
  reasonCode?: Maybe<string>;
};

export type EffectiveSettings = {
  modelId: string;
  effort?: Maybe<string>;
  serviceTier?: Maybe<string>;
  permissionProfileId: string;
  permissionLabel?: Maybe<string>;
  mode?: Maybe<string>;
  appliesTo?: Maybe<"next_turn" | "active_turn">;
  revision?: Maybe<string>;
};

export type PermissionProfile = {
  permissionProfileId: string;
  label: string;
  detail?: Maybe<string>;
  available?: Maybe<boolean>;
};

export type Artifact = {
  artifactId: string;
  taskId: string;
  name: string;
  mime?: Maybe<string>;
  sizeBytes?: Maybe<number>;
  availability?: Maybe<"available" | "host_offline" | "expired" | "blocked">;
};

export type ChangeSummary = {
  snapshotId?: Maybe<string>;
  files: Array<{ path: string; status?: Maybe<string>; additions?: Maybe<number>; deletions?: Maybe<number>; patch?: Maybe<string> }>;
};

export type TerminalPage = {
  sessionId?: Maybe<string>;
  cwd?: Maybe<string>;
  lines: Array<{ lineId?: Maybe<string>; text: string; stream?: Maybe<"stdout" | "stderr" | "system"> }>;
  exitCode?: Maybe<number | null>;
  truncated?: Maybe<boolean>;
};

export type Session = {
  authenticated: boolean;
  user?: Maybe<{ id?: Maybe<string>; name?: Maybe<string>; initials?: Maybe<string> }>;
  csrf?: Maybe<string>;
  expiresAt?: Maybe<string>;
  deviceLabel?: Maybe<string>;
  role?: Maybe<string>;
  isDemo?: Maybe<boolean>;
};

export type TaskSnapshot = {
  task: Task;
  host?: Maybe<Host>;
  project?: Maybe<Project>;
  settings?: Maybe<EffectiveSettings>;
  permissionProfiles?: Maybe<PermissionProfile[]>;
  baseSeq?: Maybe<number>;
  capabilityRevision?: Maybe<string>;
  pendingApprovalCount?: Maybe<number>;
};

export type WireEvent = {
  v?: Maybe<number>;
  type: string;
  hostId?: Maybe<string>;
  taskId?: Maybe<string>;
  turnId?: Maybe<string>;
  itemId?: Maybe<string>;
  eventId?: Maybe<string>;
  hostSeq?: Maybe<number>;
  commandId?: Maybe<string>;
  payload?: Maybe<Record<string, unknown>>;
};

export type CommandRequest = {
  commandId?: Maybe<string>;
  operation:
    | "task.create"
    | "turn.start"
    | "turn.steer"
    | "turn.interrupt"
    | "approval.respond"
    | "task.settings"
    | "task.rename"
    | "task.pin"
    | "task.archive"
    | "task.unarchive";
  projectId: string;
  taskId?: Maybe<string>;
  payload: Record<string, unknown>;
  capabilityRevision?: Maybe<string>;
  expectedRevision?: Maybe<string>;
  expectedTurnId?: Maybe<string>;
  expiresAt?: Maybe<string>;
};

export type CommandResult = {
  commandId: string;
  status: "accepted" | "dispatched" | "acknowledged" | "completed" | "rejected" | "expired" | "outcome_unknown";
  taskId?: Maybe<string>;
  message?: Maybe<string>;
  data?: Maybe<Record<string, unknown>>;
};

export type PairClaim = {
  claimId: string;
  claimSecret: string;
  state: "pending" | "approved" | "rejected" | "expired";
  expiresAt?: Maybe<string>;
  verificationWords?: Maybe<string[]>;
};

export class ApiError extends Error {
  readonly status: number | undefined;
  readonly code: string | undefined;
  readonly retryable: boolean | undefined;
  readonly requestId: string | undefined;
  readonly isNetworkError: boolean;

  constructor(message: string, options: {
    status?: Maybe<number>;
    code?: Maybe<string>;
    retryable?: Maybe<boolean>;
    requestId?: Maybe<string>;
    isNetworkError?: Maybe<boolean>;
  } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = options.status;
    this.code = options.code;
    this.retryable = options.retryable;
    this.requestId = options.requestId;
    this.isNetworkError = Boolean(options.isNetworkError);
  }
}

type Page<T> = { items: T[]; nextCursor?: Maybe<string | null> };
type ApiEnvelope<T> = { data: T; requestId?: Maybe<string> };

const now = () => new Date().toISOString();
const id = () => globalThis.crypto?.randomUUID?.() ?? `cmd-${Date.now()}-${Math.random().toString(36).slice(2)}`;

function unwrap<T>(input: unknown): T {
  if (input && typeof input === "object" && "data" in input) {
    return (input as ApiEnvelope<T>).data;
  }
  return input as T;
}

function asArray<T>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[];
  if (value && typeof value === "object") {
    const record = value as { items?: unknown[]; data?: unknown[]; hosts?: unknown[] };
    if (Array.isArray(record.items)) return record.items as T[];
    if (Array.isArray(record.data)) return record.data as T[];
    if (Array.isArray(record.hosts)) return record.hosts as T[];
  }
  return [];
}

function coercePresence(value: unknown): HostPresence {
  return value === "offline" || value === "stale" ? value : "online";
}

function coerceRuntimeState(value: unknown): TaskRuntimeState {
  if (value && typeof value === "object") {
    const type = (value as { type?: unknown }).type;
    if (type === "active") return "running";
    if (type === "notLoaded") return "not_loaded";
    if (type === "systemError") return "error";
    if (type === "idle") return "idle";
  }
  const known: TaskRuntimeState[] = ["not_loaded", "idle", "running", "waiting_approval", "waiting_input", "error"];
  return known.includes(value as TaskRuntimeState) ? (value as TaskRuntimeState) : "idle";
}

function isoTime(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return new Date(value < 100_000_000_000 ? value * 1000 : value).toISOString();
  return undefined;
}

function normaliseHost(value: Record<string, unknown>): Host {
  const rawCapabilities = value.capabilities && typeof value.capabilities === "object" ? value.capabilities as Record<string, unknown> : undefined;
  return {
    hostId: String(value.hostId ?? value.id ?? "unknown-host"),
    name: String(value.name ?? value.label ?? "电脑"),
    platform: String(value.platform ?? "Desktop"),
    adapterMode: (value.adapterMode ?? rawCapabilities?.adapterMode) as Host["adapterMode"],
    presence: value.online === false ? "offline" : coercePresence(value.presence ?? value.status),
    lastSeenAt: isoTime(value.lastSeenAt ?? value.bridgeConnectedAt),
    bridgeVersion: value.bridgeVersion as string | undefined,
    serverVersion: value.serverVersion as string | undefined,
    capabilityRevision: value.capabilityRevision as string | undefined,
    capabilities: rawCapabilities as Record<string, Capability> | undefined,
  };
}

function normaliseProject(value: Record<string, unknown>, hostId?: string): Project {
  const roots = asArray<Record<string, unknown>>(value.roots);
  const root = roots[0];
  const metadata = value.metadata && typeof value.metadata === "object" ? value.metadata as Record<string, unknown> : {};
  return {
    projectId: String(value.projectId ?? value.id ?? "unknown-project"),
    hostId: String(value.hostId ?? hostId ?? "unknown-host"),
    name: String(value.name ?? value.displayName ?? "未命名项目"),
    displayPath: (value.displayPath ?? value.path ?? root?.path ?? root?.root) as string | undefined,
    source: value.source as Project["source"],
    isGit: Boolean(value.isGit ?? value.git ?? metadata.isGit),
    branch: (value.branch ?? metadata.branch) as string | undefined,
    taskCount: typeof value.taskCount === "number" ? value.taskCount : undefined,
  };
}

function normaliseTask(value: Record<string, unknown>, hostId?: string, projectId?: string): Task {
  return {
    taskId: String(value.taskId ?? value.id ?? value.threadId ?? "unknown-task"),
    hostId: String(value.hostId ?? hostId ?? "unknown-host"),
    projectId: String(value.projectId ?? projectId ?? "unknown-project"),
    title: String(value.title ?? value.name ?? "未命名任务"),
    nativeThreadId: value.nativeThreadId as string | undefined,
    activeTurnId: value.activeTurnId as string | undefined,
    runtimeState: coerceRuntimeState(value.runtimeState ?? value.state),
    archived: Boolean(value.archived),
    pinned: Boolean(value.pinned),
    pinScope: value.pinScope as Task["pinScope"],
    canAcceptDirectInput: typeof value.canAcceptDirectInput === "boolean" ? value.canAcceptDirectInput : null,
    revision: value.revision as string | undefined,
    updatedAt: isoTime(value.updatedAt ?? value.lastUpdatedAt ?? value.recencyAt ?? value.createdAt),
    lastOutcome: value.lastOutcome as Task["lastOutcome"],
    preview: (value.preview ?? value.summary ?? value.lastMessage) as string | undefined,
    unread: Boolean(value.unread),
    pendingApprovalCount: typeof value.pendingApprovalCount === "number" ? value.pendingApprovalCount : undefined,
  };
}

function normaliseModel(value: Record<string, unknown>): ModelOption {
  const reasoning = asArray<Record<string, unknown>>(value.supportedReasoningEfforts)
    .map((entry) => String(entry.reasoningEffort ?? entry.id ?? ""))
    .filter(Boolean);
  const serviceTiers = asArray<Record<string, unknown>>(value.serviceTiers).map((tier) => ({
    id: String(tier.id ?? "default"),
    name: String(tier.name ?? tier.id ?? "默认"),
    description: typeof tier.description === "string" ? tier.description : undefined,
  }));
  return {
    modelId: String(value.modelId ?? value.model ?? value.id ?? "unknown-model"),
    label: String(value.label ?? value.displayName ?? value.model ?? value.id ?? "模型"),
    description: typeof value.description === "string" ? value.description : undefined,
    reasoningEfforts: reasoning,
    defaultEffort: typeof value.defaultReasoningEffort === "string" ? value.defaultReasoningEffort : undefined,
    serviceTiers,
    defaultServiceTier: typeof value.defaultServiceTier === "string" ? value.defaultServiceTier : undefined,
    modalities: asArray<string>(value.inputModalities),
    available: value.hidden !== true && value.available !== false,
    reasonCode: typeof value.reasonCode === "string" ? value.reasonCode : undefined,
  };
}

function normaliseThreadItem(value: Record<string, unknown>, taskId: string, turnId?: string): TimelineItem {
  const item = value.item && typeof value.item === "object" ? value.item as Record<string, unknown> : value;
  const type = String(item.type ?? "unknown");
  const textInput = asArray<Record<string, unknown>>(item.content)
    .filter((entry) => entry.type === "text")
    .map((entry) => String(entry.text ?? ""))
    .join("\n");
  const kind: TimelineKind = type === "userMessage" ? "user_message"
    : type === "agentMessage" ? "assistant_message"
      : type === "reasoning" ? "reasoning_summary"
        : type === "plan" ? "plan"
          : type === "commandExecution" ? "command"
            : type === "fileChange" ? "file_change"
              : type === "imageGeneration" || type === "imageView" ? "image"
                : type === "contextCompaction" ? "context_compaction" : "tool_call";
  const text = type === "agentMessage" || type === "plan" ? String(item.text ?? "")
    : type === "reasoning" ? asArray<string>(item.summary).join("\n")
      : type === "commandExecution" ? String(item.command ?? "")
        : type === "userMessage" ? textInput : "";
  return {
    itemId: String(item.id ?? value.itemId ?? id()),
    taskId,
    turnId: (value.turnId ?? turnId) as string | undefined,
    kind,
    state: item.status === "inProgress" ? "streaming" : "complete",
    text,
    title: type === "fileChange" ? "文件变更" : undefined,
    payload: item,
  };
}

function normaliseTimeline(value: Record<string, unknown>, taskId: string): TimelineItem {
  const rawKind = String(value.kind ?? value.type ?? "unknown");
  const validKinds: TimelineKind[] = [
    "user_message", "assistant_message", "reasoning_summary", "plan", "command", "file_change", "tool_call", "tool_result", "approval", "user_input", "image", "artifact", "context_compaction", "unknown",
  ];
  return {
    itemId: String(value.itemId ?? value.id ?? id()),
    taskId: String(value.taskId ?? taskId),
    turnId: value.turnId as string | undefined,
    sequence: typeof value.sequence === "number" ? value.sequence : undefined,
    kind: validKinds.includes(rawKind as TimelineKind) ? rawKind as TimelineKind : "unknown",
    state: value.state as TimelineItem["state"],
    createdAt: value.createdAt as string | undefined,
    text: String(value.text ?? value.content ?? value.message ?? ""),
    title: value.title as string | undefined,
    payload: (value.payload ?? value) as Record<string, unknown>,
  };
}

function normaliseApproval(value: Record<string, unknown>, taskId: string): Approval {
  return {
    approvalId: String(value.approvalId ?? value.id ?? id()),
    taskId: String(value.taskId ?? taskId),
    turnId: value.turnId as string | undefined,
    kind: (value.kind ?? "command") as Approval["kind"],
    state: (value.state ?? "pending") as Approval["state"],
    revision: value.revision as string | undefined,
    payload: (value.payload ?? value) as Record<string, unknown>,
    availableDecisions: asArray<string>(value.availableDecisions ?? value.decisions),
  };
}

function productCommand(command: Required<Pick<CommandRequest, "commandId" | "expiresAt">> & CommandRequest): { type: string; payload: Record<string, unknown> } {
  const taskId = command.taskId;
  switch (command.operation) {
    case "task.create":
      return { type: "task.create", payload: { projectId: command.projectId, text: String(command.payload.text ?? ""), modelId: command.payload.modelId, effort: command.payload.effort, serviceTier: command.payload.serviceTier, permissionProfileId: command.payload.permissionProfileId } };
    case "turn.start":
      if (!taskId) throw new ApiError("发送消息时缺少任务标识。", { code: "TASK_ID_REQUIRED" });
      return { type: "turn.start", payload: { threadId: taskId, text: String(command.payload.text ?? ""), model: command.payload.modelId, effort: command.payload.effort, serviceTier: command.payload.serviceTier } };
    case "turn.steer":
      if (!taskId || !command.expectedTurnId) throw new ApiError("跟进运行中的任务需要当前回合标识。", { code: "ACTIVE_TURN_REQUIRED" });
      return { type: "turn.steer", payload: { threadId: taskId, expectedTurnId: command.expectedTurnId, text: String(command.payload.text ?? "") } };
    case "turn.interrupt":
      if (!taskId || !command.expectedTurnId) throw new ApiError("停止任务需要当前回合标识。", { code: "ACTIVE_TURN_REQUIRED" });
      return { type: "turn.interrupt", payload: { threadId: taskId, turnId: command.expectedTurnId } };
    case "task.rename":
      if (!taskId) throw new ApiError("重命名任务时缺少任务标识。", { code: "TASK_ID_REQUIRED" });
      return { type: "task.rename", payload: { threadId: taskId, name: String(command.payload.name ?? "") } };
    case "task.archive":
    case "task.unarchive":
      if (!taskId) throw new ApiError("归档任务时缺少任务标识。", { code: "TASK_ID_REQUIRED" });
      return { type: command.operation, payload: { threadId: taskId } };
    case "task.settings":
      throw new ApiError("当前 Bridge 只读取模型设置；写入会在桌面同任务接入验证后开放。", { code: "SETTINGS_WRITE_UNAVAILABLE" });
    case "approval.respond":
      throw new ApiError("当前 Bridge 尚未验证此类审批的回复格式。", { code: "APPROVAL_RESPONSE_UNAVAILABLE" });
    case "task.pin":
      throw new ApiError("当前 Bridge 不会把远控收藏伪装成桌面置顶。", { code: "PIN_UNAVAILABLE" });
  }
}

function stringFromResult(value: Record<string, unknown>, paths: string[]): string | undefined {
  for (const path of paths) {
    let current: unknown = value;
    for (const part of path.split(".")) {
      current = current && typeof current === "object" ? (current as Record<string, unknown>)[part] : undefined;
    }
    if (typeof current === "string" && current) return current;
  }
  return undefined;
}

const DEMO_HOST: Host = {
  hostId: "host-studio",
  name: "Studio Mac",
  platform: "macOS",
  adapterMode: "desktop_attached",
  presence: "online",
  lastSeenAt: now(),
  bridgeVersion: "0.1.0",
  capabilityRevision: "demo-r3",
  capabilities: {
    "turn.interrupt": { state: "available", source: "bridge" },
    "approval.respond": { state: "available", source: "bridge" },
    "task.settings": { state: "available", source: "bridge" },
    terminal: { state: "available", source: "bridge" },
  },
};

const DEMO_PROJECTS: Project[] = [
  { projectId: "project-relay", hostId: DEMO_HOST.hostId, name: "remote-control", displayPath: "~/Workspace/remote-control", isGit: true, branch: "main", taskCount: 5 },
  { projectId: "project-notes", hostId: DEMO_HOST.hostId, name: "product-notes", displayPath: "~/Workspace/product-notes", isGit: true, branch: "design/mobile", taskCount: 2 },
  { projectId: "project-sandbox", hostId: DEMO_HOST.hostId, name: "sandbox", displayPath: "~/Workspace/sandbox", isGit: false, taskCount: 1 },
];

const DEMO_TASKS: Task[] = [
  {
    taskId: "task-reconnect", hostId: DEMO_HOST.hostId, projectId: "project-relay", title: "检查重连后的事件顺序", runtimeState: "waiting_approval", activeTurnId: "turn-92", revision: "8", updatedAt: now(), preview: "已整理恢复顺序，等待一次命令确认。", pendingApprovalCount: 1, pinned: true, pinScope: "remote_only",
  },
  {
    taskId: "task-mobile", hostId: DEMO_HOST.hostId, projectId: "project-relay", title: "优化窄屏编辑器状态", runtimeState: "running", activeTurnId: "turn-90", revision: "6", updatedAt: new Date(Date.now() - 1000 * 60 * 5).toISOString(), preview: "正在检查安全区与软键盘变化。",
  },
  {
    taskId: "task-audit", hostId: DEMO_HOST.hostId, projectId: "project-relay", title: "复查配对错误提示", runtimeState: "idle", lastOutcome: "completed", revision: "3", updatedAt: new Date(Date.now() - 1000 * 60 * 32).toISOString(), preview: "已完成错误状态的可读性检查。",
  },
  {
    taskId: "task-empty", hostId: DEMO_HOST.hostId, projectId: "project-relay", title: "整理本周回归范围", runtimeState: "idle", revision: "1", updatedAt: new Date(Date.now() - 1000 * 60 * 95).toISOString(), preview: "等待新的输入。",
  },
  {
    taskId: "task-notes", hostId: DEMO_HOST.hostId, projectId: "project-notes", title: "汇总移动端验收问题", runtimeState: "idle", revision: "3", updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(), preview: "包含三个待确认项。",
  },
];

const DEMO_SETTINGS: EffectiveSettings = {
  modelId: "balanced",
  effort: "标准",
  serviceTier: "balanced",
  permissionProfileId: "ask",
  permissionLabel: "操作前询问",
  appliesTo: "next_turn",
  revision: "settings-5",
};

const DEMO_MODELS: ModelOption[] = [
  {
    modelId: "balanced", label: "Balanced", description: "适合多数开发任务", reasoningEfforts: ["低", "标准", "深入"], defaultEffort: "标准", serviceTiers: [{ id: "balanced", name: "平衡", description: "稳定的响应速度" }, { id: "priority", name: "优先", description: "优先处理下一轮" }], defaultServiceTier: "balanced", available: true,
  },
  {
    modelId: "focused", label: "Focused", description: "适合需要较长推理的任务", reasoningEfforts: ["标准", "深入", "最大"], defaultEffort: "深入", serviceTiers: [{ id: "balanced", name: "平衡" }], defaultServiceTier: "balanced", available: true,
  },
];

const DEMO_PROFILES: PermissionProfile[] = [
  { permissionProfileId: "ask", label: "操作前询问", detail: "涉及命令或文件变更时请求确认", available: true },
  { permissionProfileId: "workspace", label: "工作区内允许", detail: "仅在当前项目目录内自动执行", available: true },
  { permissionProfileId: "readonly", label: "只读", detail: "可以分析，不能修改项目文件", available: true },
];

const DEMO_TIMELINE: Record<string, TimelineItem[]> = {
  "task-reconnect": [
    { itemId: "i-1", taskId: "task-reconnect", kind: "user_message", createdAt: new Date(Date.now() - 1000 * 60 * 18).toISOString(), text: "检查断线恢复时，事件是否会重复展示。" },
    { itemId: "i-2", taskId: "task-reconnect", kind: "reasoning_summary", createdAt: new Date(Date.now() - 1000 * 60 * 17).toISOString(), text: "正在比对快照序号与增量序号。" },
    { itemId: "i-3", taskId: "task-reconnect", kind: "assistant_message", createdAt: new Date(Date.now() - 1000 * 60 * 13).toISOString(), text: "已确认需要以稳定 item ID 去重，并在快照完成后再接收后续增量。接下来我会运行现有回归检查。" },
    { itemId: "i-4", taskId: "task-reconnect", kind: "command", state: "complete", createdAt: new Date(Date.now() - 1000 * 60 * 11).toISOString(), title: "读取当前事件处理器", text: "检查 event-reducer.ts", payload: { cwd: "remote-control", output: "已读取 1 个文件", exitCode: 0 } },
    { itemId: "i-5", taskId: "task-reconnect", kind: "file_change", state: "complete", createdAt: new Date(Date.now() - 1000 * 60 * 4).toISOString(), title: "1 个文件有改动", text: "事件去重与序号恢复", payload: { additions: 18, deletions: 4, files: 1 } },
    { itemId: "i-6", taskId: "task-reconnect", kind: "assistant_message", state: "streaming", createdAt: now(), text: "恢复过程已补上。运行检查前，需要确认一次本地命令。" },
  ],
  "task-mobile": [
    { itemId: "m-1", taskId: "task-mobile", kind: "user_message", text: "让窄屏下输入框保持可用。", createdAt: new Date(Date.now() - 1000 * 60 * 12).toISOString() },
    { itemId: "m-2", taskId: "task-mobile", kind: "assistant_message", state: "streaming", text: "正在检查可视视口变化和底部安全区。", createdAt: now() },
  ],
};

const DEMO_APPROVALS: Record<string, Approval[]> = {
  "task-reconnect": [{
    approvalId: "approval-verify", taskId: "task-reconnect", turnId: "turn-92", kind: "command", state: "pending", revision: "approval-2", availableDecisions: ["approve", "deny"], payload: { command: "pnpm test --filter event-reducer", cwd: "remote-control", summary: "运行现有事件恢复回归检查" },
  }],
};

const DEMO_CHANGES: Record<string, ChangeSummary> = {
  "task-reconnect": {
    snapshotId: "snapshot-14",
    files: [{ path: "src/events/reducer.ts", status: "modified", additions: 18, deletions: 4, patch: "@@ reconnect\n+ preserve last host sequence\n+ ignore known item identifiers\n- replay every buffered item" }],
  },
};

const DEMO_TERMINAL: Record<string, TerminalPage> = {
  "task-reconnect": { cwd: "~/Workspace/remote-control", lines: [
    { stream: "system", text: "$ pnpm test --filter event-reducer" },
    { stream: "stdout", text: "waiting for approval before dispatch" },
  ], exitCode: null },
};

const DEMO_ARTIFACTS: Record<string, Artifact[]> = {
  "task-reconnect": [{ artifactId: "artifact-summary", taskId: "task-reconnect", name: "recovery-notes.md", mime: "text/markdown", sizeBytes: 1820, availability: "available" }],
};

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export class ApiClient {
  private readonly base: string;
  private csrf: string | undefined;
  private mockEnabled = false;
  private readonly snapshots = new Map<string, Record<string, unknown>>();

  constructor(base = "/api/v1") {
    this.base = base.replace(/\/$/, "");
  }

  get usingMock(): boolean {
    return this.mockEnabled;
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set("accept", "application/json");
    if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json");
    if (this.csrf && !["GET", "HEAD"].includes((init.method ?? "GET").toUpperCase())) headers.set("x-csrf-token", this.csrf);

    let response: Response;
    try {
      response = await fetch(`${this.base}${path}`, { ...init, headers, credentials: "include" });
    } catch (error) {
      throw new ApiError("无法连接远程服务。", { code: "NETWORK", isNetworkError: true, retryable: true });
    }

    const text = await response.text();
    let payload: unknown = null;
    try {
      payload = text ? JSON.parse(text) : null;
    } catch {
      if (!response.ok) throw new ApiError("服务返回了无法识别的响应。", { status: response.status, code: "INVALID_RESPONSE", retryable: response.status >= 500 });
      throw new ApiError("服务返回了无法识别的响应。", { status: response.status, code: "INVALID_RESPONSE" });
    }

    if (!response.ok) {
      const error = (payload as { error?: { code?: string; message?: string; retryable?: boolean }; requestId?: string })?.error;
      throw new ApiError(error?.message ?? `请求失败（${response.status}）`, {
        status: response.status,
        code: error?.code,
        retryable: error?.retryable,
        requestId: (payload as { requestId?: string })?.requestId,
      });
    }
    return unwrap<T>(payload);
  }

  private async fallback<T>(live: () => Promise<T>, mock: () => T): Promise<T> {
    try {
      return await live();
    } catch (error) {
      const apiError = error as ApiError;
      // A static Pages preview has no API routes. Do not hide an authenticated
      // API error (401/403) or a valid JSON business error behind demo data.
      if (apiError.isNetworkError || apiError.code === "INVALID_RESPONSE") {
        this.mockEnabled = true;
        return mock();
      }
      throw error;
    }
  }

  async getSession(): Promise<Session> {
    try {
      const value = await this.request<Record<string, unknown>>("/session");
      const source = value.session && typeof value.session === "object" ? value.session as Record<string, unknown> : value;
      const session: Session = {
        authenticated: source.authenticated !== false && Boolean(source.deviceId ?? source.user ?? source.role ?? source.csrf),
        user: source.user as Session["user"],
        csrf: source.csrf as string | undefined,
        expiresAt: isoTime(source.expiresAt),
        deviceLabel: source.deviceLabel as string | undefined,
        role: source.role as string | undefined,
      };
      this.csrf = session.csrf;
      return session;
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError.status === 401 || apiError.status === 403) return { authenticated: false };
      if (apiError.isNetworkError || apiError.code === "INVALID_RESPONSE") {
        this.mockEnabled = true;
        return { authenticated: true, isDemo: true, csrf: "preview", user: { name: "本机预览", initials: "预" }, deviceLabel: "此浏览器" };
      }
      throw error;
    }
  }

  async getHosts(): Promise<Host[]> {
    return this.fallback(async () => asArray<Record<string, unknown>>(await this.request<unknown>("/hosts")).map(normaliseHost), () => clone([DEMO_HOST]));
  }

  async getHost(hostId: string): Promise<Host> {
    return this.fallback(async () => {
      const host = (await this.getHosts()).find((item) => item.hostId === hostId);
      if (!host) throw new ApiError("找不到已授权的电脑。", { status: 404, code: "HOST_NOT_FOUND" });
      return host;
    }, () => clone(DEMO_HOST));
  }

  async getProjects(hostId: string): Promise<Project[]> {
    return this.fallback(
      async () => asArray<Record<string, unknown>>((await this.getSnapshot(hostId)).projects).map((item) => normaliseProject(item, hostId)),
      () => clone(DEMO_PROJECTS.filter((project) => project.hostId === hostId)),
    );
  }

  async getTasks(hostId: string, options: { projectId?: string; query?: string; archived?: boolean; cursor?: string } = {}): Promise<Page<Task>> {
    return this.fallback(async () => {
      const snapshot = await this.getSnapshot(hostId);
      const needle = options.query?.trim().toLocaleLowerCase();
      const items = asArray<Record<string, unknown>>(snapshot.threads)
        .map((item) => normaliseTask(item, hostId, options.projectId))
        .filter((item) => (!options.projectId || item.projectId === options.projectId)
          && (!needle || item.title.toLocaleLowerCase().includes(needle))
          && (options.archived ? item.archived : !item.archived));
      for (const item of items) this.snapshots.set(`task:${item.taskId}`, { hostId });
      return { items, nextCursor: null };
    }, () => {
      const needle = options.query?.trim().toLocaleLowerCase();
      return {
        items: clone(DEMO_TASKS.filter((task) => task.hostId === hostId && (!options.projectId || task.projectId === options.projectId) && (!needle || task.title.toLocaleLowerCase().includes(needle)) && (options.archived ? task.archived : !task.archived))),
        nextCursor: null,
      };
    });
  }

  async getTask(hostId: string, taskId: string): Promise<TaskSnapshot> {
    return this.fallback(async () => {
      const [snapshot, host] = await Promise.all([this.getSnapshot(hostId), this.getHost(hostId)]);
      const taskRaw = asArray<Record<string, unknown>>(snapshot.threads).find((item) => String(item.id ?? item.threadId) === taskId);
      if (!taskRaw) throw new ApiError("该任务不在当前电脑的同步快照中。", { status: 404, code: "TASK_NOT_FOUND" });
      const task = normaliseTask(taskRaw, hostId);
      const projectRaw = asArray<Record<string, unknown>>(snapshot.projects).find((item) => String(item.id ?? item.projectId) === task.projectId);
      return {
        task,
        host,
        project: projectRaw ? normaliseProject(projectRaw, task.hostId) : undefined,
        baseSeq: asArray<unknown>(snapshot.events).length,
        capabilityRevision: host.capabilityRevision,
        pendingApprovalCount: 0,
      };
    }, () => {
      const fallbackTask = DEMO_TASKS.find((entry) => entry.taskId === taskId) ?? DEMO_TASKS[0];
      if (!fallbackTask) throw new ApiError("演示任务不可用。", { code: "DEMO_UNAVAILABLE" });
      const task = clone(fallbackTask);
      return { task, host: clone(DEMO_HOST), project: clone(DEMO_PROJECTS.find((entry) => entry.projectId === task.projectId) ?? DEMO_PROJECTS[0]), settings: clone(DEMO_SETTINGS), permissionProfiles: clone(DEMO_PROFILES), baseSeq: 0, capabilityRevision: DEMO_HOST.capabilityRevision, pendingApprovalCount: DEMO_APPROVALS[taskId]?.filter((approval) => approval.state === "pending").length ?? 0 };
    });
  }

  async getTimeline(hostId: string, taskId: string, cursor?: string): Promise<Page<TimelineItem>> {
    return this.fallback(async () => {
      const value = await this.dispatchAndPoll(hostId, "thread.items.list", { threadId: taskId, cursor: cursor ?? null, limit: 50, sortDirection: "asc" });
      return {
        items: asArray<Record<string, unknown>>(value).map((item) => normaliseThreadItem(item, taskId)),
        nextCursor: value.nextCursor as string | null | undefined,
      };
    }, () => ({ items: clone(DEMO_TIMELINE[taskId] ?? []), nextCursor: null }));
  }

  async getApprovals(taskId: string): Promise<Approval[]> {
    // Approval response schemas are app-server-method-specific.  Until the
    // Bridge persists typed pending requests, it is safer to expose none than
    // to manufacture an approval that could be answered incorrectly.
    return this.fallback(async () => [], () => clone(DEMO_APPROVALS[taskId] ?? []));
  }

  async getModels(hostId: string, capabilityRevision?: string): Promise<ModelOption[]> {
    void capabilityRevision;
    return this.fallback(async () => asArray<Record<string, unknown>>((await this.getSnapshot(hostId)).models).map(normaliseModel), () => clone(DEMO_MODELS));
  }

  async getChanges(taskId: string): Promise<ChangeSummary> {
    return this.fallback<ChangeSummary>(async () => ({ files: [] }), () => clone(DEMO_CHANGES[taskId] ?? { files: [] }));
  }

  async getTerminal(taskId: string): Promise<TerminalPage> {
    return this.fallback<TerminalPage>(async () => ({ lines: [] }), () => clone(DEMO_TERMINAL[taskId] ?? { lines: [] }));
  }

  async getArtifacts(taskId: string): Promise<Artifact[]> {
    return this.fallback(async () => [], () => clone(DEMO_ARTIFACTS[taskId] ?? []));
  }

  async claimPair(hostId: string, token: string, deviceLabel: string): Promise<Session> {
    return this.fallback(async () => {
      await this.request<Record<string, unknown>>("/pairings/claim", { method: "POST", body: JSON.stringify({ hostId, token, deviceLabel }) });
      const session = await this.getSession();
      if (!session.authenticated) throw new ApiError("配对完成后未获得浏览器会话。", { code: "PAIRING_SESSION_MISSING" });
      return { ...session, deviceLabel };
    }, () => ({ authenticated: true, isDemo: true, csrf: "preview", user: { name: "本机预览", initials: "预" }, deviceLabel }));
  }

  async command(hostId: string, input: CommandRequest): Promise<CommandResult> {
    const command: Required<Pick<CommandRequest, "commandId" | "expiresAt">> & CommandRequest = {
      ...input,
      commandId: input.commandId ?? id(),
      expiresAt: input.expiresAt ?? new Date(Date.now() + 5 * 60_000).toISOString(),
    };
    const commandId = command.commandId ?? id();
    return this.fallback<CommandResult>(async () => {
      const wire = productCommand(command);
      const data = await this.dispatchAndPoll(hostId, wire.type, wire.payload, commandId);
      const taskId = stringFromResult(data, ["taskId", "threadId", "thread.id"]);
      return { commandId, status: "completed", taskId, data };
    }, () => this.mockCommand(command));
  }

  private async getSnapshot(hostId: string): Promise<Record<string, unknown>> {
    const value = await this.request<Record<string, unknown>>(`/hosts/${encodeURIComponent(hostId)}/snapshot`);
    const snapshot = value.snapshot && typeof value.snapshot === "object" && !Array.isArray(value.snapshot)
      ? value.snapshot as Record<string, unknown> : {};
    this.snapshots.set(hostId, snapshot);
    return snapshot;
  }

  private async dispatchAndPoll(hostId: string, type: string, payload: Record<string, unknown>, suppliedCommandId?: string): Promise<Record<string, unknown>> {
    const commandId = suppliedCommandId ?? id();
    await this.request<Record<string, unknown>>(`/hosts/${encodeURIComponent(hostId)}/commands`, {
      method: "POST",
      body: JSON.stringify({ commandId, type, payload }),
    });
    for (let attempt = 0; attempt < 40; attempt += 1) {
      await new Promise<void>((resolve) => window.setTimeout(resolve, 250));
      const value = await this.request<Record<string, unknown>>(`/hosts/${encodeURIComponent(hostId)}/commands/${encodeURIComponent(commandId)}`);
      const command = value.command && typeof value.command === "object" ? value.command as Record<string, unknown> : value;
      if (command.status === "completed") {
        const result = command.result && typeof command.result === "object" ? command.result as Record<string, unknown> : {};
        if (result.ok === false) throw new ApiError(String(result.message ?? result.code ?? "电脑拒绝了该请求。"), { code: String(result.code ?? "BRIDGE_REJECTED") });
        return result.result && typeof result.result === "object" ? result.result as Record<string, unknown> : result;
      }
      if (command.status === "failed" || command.status === "unknown") {
        const result = command.result && typeof command.result === "object" ? command.result as Record<string, unknown> : {};
        throw new ApiError(String(result.message ?? result.code ?? "电脑未能完成该请求。"), { code: String(result.code ?? "COMMAND_FAILED") });
      }
    }
    throw new ApiError("电脑尚未返回结果；请求没有被重新发送。", { code: "COMMAND_PENDING", retryable: true });
  }

  private mockCommand(command: Required<Pick<CommandRequest, "commandId" | "expiresAt">> & CommandRequest): CommandResult {
    const taskId = command.taskId;
    if (command.operation === "turn.interrupt" && taskId) {
      const task = DEMO_TASKS.find((entry) => entry.taskId === taskId);
      if (task) { task.runtimeState = "idle"; task.lastOutcome = "interrupted"; task.updatedAt = now(); }
    }
    if (command.operation === "approval.respond" && taskId) {
      const approvalId = String(command.payload.approvalId ?? "");
      const approval = (DEMO_APPROVALS[taskId] ?? []).find((entry) => entry.approvalId === approvalId);
      if (approval) approval.state = "resolved";
      const task = DEMO_TASKS.find((entry) => entry.taskId === taskId);
      if (task) task.runtimeState = "running";
    }
    if (command.operation === "task.settings" && command.payload.modelId) {
      DEMO_SETTINGS.modelId = String(command.payload.modelId);
      DEMO_SETTINGS.effort = command.payload.effort ? String(command.payload.effort) : DEMO_SETTINGS.effort;
      DEMO_SETTINGS.serviceTier = command.payload.serviceTier ? String(command.payload.serviceTier) : DEMO_SETTINGS.serviceTier;
      DEMO_SETTINGS.permissionProfileId = command.payload.permissionProfileId ? String(command.payload.permissionProfileId) : DEMO_SETTINGS.permissionProfileId;
      DEMO_SETTINGS.permissionLabel = DEMO_PROFILES.find((profile) => profile.permissionProfileId === DEMO_SETTINGS.permissionProfileId)?.label;
    }
    if ((command.operation === "turn.start" || command.operation === "turn.steer") && taskId) {
      const text = String(command.payload.text ?? "").trim();
      if (text) {
        const stream: TimelineItem = { itemId: id(), taskId, kind: "user_message", text, createdAt: now(), state: "complete" };
        const response: TimelineItem = { itemId: id(), taskId, kind: "assistant_message", text: "已收到这条跟进，正在继续处理。", createdAt: now(), state: "streaming" };
        (DEMO_TIMELINE[taskId] ??= []).push(stream, response);
        const task = DEMO_TASKS.find((entry) => entry.taskId === taskId);
        if (task) { task.runtimeState = "running"; task.activeTurnId = `turn-${Date.now()}`; task.updatedAt = now(); }
      }
    }
    return { commandId: command.commandId ?? id(), status: "accepted", taskId, message: "已交给已连接的电脑。" };
  }

  /**
   * Opens the browser subscription channel. Mutations remain HTTP commands so
   * reconnects never turn a replayed socket frame into a duplicate action.
   */
  openHostEvents(hostId: string, handlers: {
    onOpen?: () => void;
    onEvent?: (event: WireEvent) => void;
    onClose?: (event: CloseEvent) => void;
    onError?: () => void;
  }): { close: () => void; subscribe: (taskIds?: string[], afterHostSeq?: number) => void } {
    const scheme = globalThis.location?.protocol === "https:" ? "wss:" : "ws:";
    const origin = globalThis.location?.host ?? "localhost";
    const url = `${scheme}//${origin}${this.base}/ws?hostId=${encodeURIComponent(hostId)}`;
    let socket: WebSocket | undefined;
    try {
      socket = new WebSocket(url);
      socket.addEventListener("open", () => handlers.onOpen?.());
      socket.addEventListener("message", (message) => {
        try {
          const frame = normaliseWireFrame(JSON.parse(String(message.data)));
          if (frame) handlers.onEvent?.(frame);
        } catch {
          // Ignore a malformed single frame; a subsequent resync can recover.
        }
      });
      socket.addEventListener("close", (event) => handlers.onClose?.(event));
      socket.addEventListener("error", () => handlers.onError?.());
    } catch {
      queueMicrotask(() => handlers.onError?.());
    }
    return {
      close: () => socket?.close(1000, "view closed"),
      subscribe: (taskIds, afterHostSeq) => {
        if (socket?.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ kind: "subscribe", taskIds, afterHostSeq }));
        }
      },
    };
  }
}

function normaliseWireFrame(value: unknown): WireEvent | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const frame = value as Record<string, unknown>;
  if (frame.kind === "host.status") return { type: "host.presence", payload: { presence: frame.online === true ? "online" : "offline" } };
  if (frame.kind === "snapshot") return { type: "snapshot", payload: frame.snapshot as Record<string, unknown> | undefined };
  if (frame.kind !== "event" || !frame.event || typeof frame.event !== "object" || Array.isArray(frame.event)) return null;
  const event = frame.event as Record<string, unknown>;
  if (event.type !== "app-server.notification") return { type: String(event.type ?? "event"), payload: event };
  const method = String(event.method ?? "");
  const params = event.params && typeof event.params === "object" && !Array.isArray(event.params) ? event.params as Record<string, unknown> : {};
  const type = method === "item/agentMessage/delta" ? "item.delta" : method.replaceAll("/", ".");
  const item = params.item && typeof params.item === "object" ? params.item as Record<string, unknown> : {};
  return {
    type,
    taskId: typeof params.threadId === "string" ? params.threadId : undefined,
    turnId: typeof params.turnId === "string" ? params.turnId : undefined,
    itemId: typeof item.id === "string" ? item.id : undefined,
    payload: { ...params, ...item },
  };
}

export const api = new ApiClient();
